import { useRouter } from 'next/router'
import { ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { RootState, useSelector, useDispatch } from '@/store'
import { setContactIr } from '@/store/contactIr/slice'
import { ContactIr } from '@/store/contactIr/types'
import { theme } from '@/styles/theme'

import { irLabels } from './IrLabel'
import { Button } from '@/components/elements/Button'
import { FormInput } from '@/components/elements/enFormInput'
import { FormLabel } from '@/components/elements/enFormLabel'
import { LinkText } from '@/components/elements/LinkText'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'


export const IrPage = () => {
  const form = useSelector((state: RootState) => state.contactIr)
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactIr>()
  const router = useRouter()

  const onSubmit = (data: ContactIr) => {
    dispatch(setContactIr(data))
    router.push('/en/contact/ir/confirm')
  }

  return (
    <>
      <Head
        title="Shareholders and Investors"
        description="For inquiries about information for shareholders and investors."
        ogp={{ type: 'article', url: 'en/contact/ir/' }}
      />
      <IRMain>
        <SectionTitle
          enTitleText="Investors / Shareholders"
          subTitleText=""
          color="normal"
        />
        <Note>
          <p>
           Inquiry form for IR (Shareholers and Investors information).
          </p>
          <p>
            Before contacting us, please refer to the &quot;
            <LinkText path="/en/ir/faq" text="Frequently Asked Questions" />
            &quot; section.  
          </p>
          <p>
            Please note that we cannot respond to inquiries other than those related to IR, so please understand in advance. 
          </p>
        </Note>
        <Form method="post" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormBackground>
            <FormMain>
              <FormLabelWrapper>
                <FormLabel {...irLabels.company} />
              </FormLabelWrapper>
              <FormInput
                id="company"
                type="text"
                register={register('company')}
                defaultValue={form.company}
              />
              <FormLabelWrapper>
                <FormLabel {...irLabels.fullName} />
              </FormLabelWrapper>
              <FormInner>
                <FormGroup>
                  <FormLabel {...irLabels.lastName} />
                  <FormInput
                    id="lastName"
                    type="text"
                    register={register('lastName', {
                      required: {
                        value: true,
                        message: 'Please fill the required field.',
                      },
                    })}
                    errorMessage={errors.lastName}
                    defaultValue={form.lastName}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel {...irLabels.firstName} />
                  <FormInput
                    id="firstName"
                    type="text"
                    register={register('firstName', {
                      required: {
                        value: true,
                        message: 'Please fill the required field.',
                      },
                    })}
                    errorMessage={errors.firstName}
                    defaultValue={form.firstName}
                  />
                </FormGroup>
              </FormInner>
              <FormLabelWrapper>
                <FormLabel {...irLabels.email} />
              </FormLabelWrapper>
              <FormInput
                id="email"
                type="email"
                register={register('email', {
                  required: {
                    value: true,
                    message: 'Please fill the required field.',
                  },
                  pattern: {
                    message: 'Please enter correctly.',
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  },
                })}
                placeholder="e.g. XXX@XXX.ne.jp"
                errorMessage={errors.email}
                defaultValue={form.email}
              />
              <FormLabelWrapper>
                <FormLabel {...irLabels.tel} />
              </FormLabelWrapper>
              <FormInput
                id="tel"
                type="tel"
                maxLength={13}
                register={register('tel', {
                  pattern: {
                    message: 'Please enter correctly.',
                    value: /^[-0-9]+$/,
                  },
                })}
                placeholder="e.g. 00-0000-0000"
                errorMessage={errors.tel}
                defaultValue={form.tel}
              />
              <FormLabelWrapper>
                <FormLabel {...irLabels.title} />
              </FormLabelWrapper>
              <FormInput
                id="title"
                type="text"
                register={register('title')}
                placeholder="e.g. Subject of the contents"
                defaultValue={form.title}
              />
              <FormLabelWrapper>
                <FormLabel {...irLabels.detail} />
              </FormLabelWrapper>
              <FormInput
                id="detail"
                type="text"
                register={register('detail', {
                  required: {
                    value: true,
                    message: 'Please fill the required field.',
                  },
                })}
                as="textarea"
                placeholder="Please enter the detail of your inquiry."
                errorMessage={errors.detail}
                defaultValue={form.detail}
              />
            </FormMain>
            <FormBottom>
              Personal information acquired from this inquiry form is used solely for the purpose of responding to your inquiry. 
              <br />
              The Company properly handles personal informarion according to its <span/>

              {/* <Link href="/legal/privacy-policy/" passHref>
                <a> Privacy Policy</a>
              </Link> */}
              <LinkText path="/en/legal/privacy-policy/" text="Privacy Policy" />
              .
            </FormBottom>
          </FormBackground>
          <ButtonWrapper>
            <SubmitButton btnStyle="outline" type="submit">
              Confirm
            </SubmitButton>
          </ButtonWrapper>
        </Form>
      </IRMain>
    </>
  )
}

IrPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Inquiry',
    subTitle: '',
  }

  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Inquiry', path: '/en/contact/' },
    { name: 'Investors / Shareholders', path: '' },
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const IRMain = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 78px auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 40px auto 0;
    padding: 0 20px 70px;
  }
`

const Note = styled.div`
  margin: 71px 0 77px;

  p {
    line-height: 30px;
    letter-spacing: 0.32px;
    font-weight: 400;
  }

  @media screen and (max-width: 1047px) {
    margin: 56px 0 45px;

    p {
      font-size: 15px;
      line-height: 25px;
      letter-spacing: 0.3px;
    }
  }
`

const Form = styled.form``

const FormMain = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 40px 30px;

  @media screen and (max-width: 1047px) {
    grid-template-columns: 1fr;
    gap: 16px 0;
  }
`

const FormBackground = styled.div`
  background-color: ${theme.colors.backgroundGrey};
  padding: 86px 74px 99px;

  @media screen and (max-width: 1047px) {
    padding: 27px 20px 40px;
  }
`

const FormLabelWrapper = styled.div`
  padding-top: 14px;
`

const FormInner = styled.div`
  display: flex;
  align-items: center;
  column-gap: 43px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 14px;
  }
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 26px;
  width: 50%;

  div {
    flex: 1;
  }

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const FormBottom = styled.p`
  line-height: 26px;
  letter-spacing: 0.32px;
  margin-top: 74px;
  font-weight: 400;

  @media screen and (max-width: 1047px) {
    margin-top: 36px;
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 107px;

  @media screen and (max-width: 1047px) {
    margin-top: 54px;
  }
`

const SubmitButton = styled(Button)`
  width: 198px;
  font-weight: 400;
  border-width: 1px;
`
